import { LoadingSpinner } from "@clipboard-health/ui-components";
import { Stack, useTheme } from "@mui/material";
import { ErrorPageTemplate } from "@src/appV2/Errors/Template";
import { ClipboardLogo } from "@src/appV2/lib/Content/Logo/Logo";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useEffect } from "react";

import {
  type OnboardingStepProps,
  OnboardingStepWrapper,
  type OnboardingStepWrapperProps,
} from "../OnboardingStepWrapper";
import { InterestedWorkTypeStepContent } from "./InterestedWorkTypeStepContent";
import { useAskInterestedWorkType } from "./useAskInterestedWorkType";

export function InterestedWorkTypeStep(
  props: Pick<OnboardingStepWrapperProps, "worker" | "progress" | "onBack" | "onNext" | "stage">
) {
  const { worker, progress, onBack, onNext, stage } = props;

  const stepProps = {
    worker,
    progress,
    stage,
    onNext,
    onBack,
  };

  const { data: canAskInterestedWorkType, status } = useAskInterestedWorkType({
    workerId: worker?.userId ?? "",
  });

  const theme = useTheme();

  useEffect(() => {
    if (status === "success" && !canAskInterestedWorkType) {
      void onBack();
    }
  }, [status, canAskInterestedWorkType, onBack]);

  if (status === "success" && canAskInterestedWorkType) {
    return (
      <OnboardingStepWrapper
        {...stepProps}
        render={(props: OnboardingStepProps) => <InterestedWorkTypeStepContent {...props} />}
      />
    );
  }

  if (status === "error") {
    return (
      <OnboardingStepWrapper
        {...stepProps}
        render={() => (
          <ErrorPageTemplate
            illustration={<ClipboardLogo />}
            sx={{
              background: theme.palette.background.tertiary,
            }}
            title="We're having trouble loading page"
            description="Please try again"
            actions={
              <Stack spacing={4} width="100%">
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => {
                    globalThis.location.reload();
                  }}
                >
                  Reload page
                </Button>
              </Stack>
            }
          />
        )}
      />
    );
  }

  return (
    <OnboardingStepWrapper
      {...stepProps}
      render={() => (
        <PageContent>
          <Stack spacing={3} alignItems="center" justifyContent="center" height="100%" width="100%">
            <LoadingSpinner size="large" />
          </Stack>
        </PageContent>
      )}
    />
  );
}
